import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PersonalInfoRequest } from '@/types';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { API_CONFIG } from '@/config';

interface PersonalInfoRequestDialogProps {
  request: PersonalInfoRequest;
  open: boolean;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
}

export function PersonalInfoRequestDialog({
  request,
  open,
  onClose,
  onApprove,
  onReject,
}: PersonalInfoRequestDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleApprove = async () => {
    try {
      setIsSubmitting(true);
      await onApprove();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>مراجعة طلب إضافة من أنت</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-6">
            {/* Requester Info */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">معلومات مقدم الطلب</h3>
              <p className="text-sm text-gray-600">الاسم: {request.requester_name}</p>
              <p className="text-sm text-gray-600">رقم الهاتف: {request.phone_number}</p>
            </div>

            {/* Personal Info */}
            <div className="space-y-4">
              <h3 className="font-semibold">المعلومات المطلوب إضافتها</h3>
              <div className="bg-white p-4 rounded-lg border space-y-4">
                <p className="font-medium">{request.full_name}</p>
                <p className="text-gray-600">{request.description}</p>
                
                {/* Photo Preview */}
                {request.photo_url && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">الصورة الشخصية:</h4>
                    <div className="w-32 h-32 rounded-lg overflow-hidden border">
                      <img 
                        src={request.photo_url ? API_CONFIG.BASE_URL + "/" + request.photo_url : ""}
                        alt={request.full_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}

                {/* Resume Preview */}
                {request.resume_url && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">السيرة الذاتية:</h4>
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border">
                      <a 
                        href={request.resume_url ? API_CONFIG.BASE_URL + "/" + request.resume_url : ""}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        عرض ملف السيرة الذاتية
                      </a>
                    </div>
                    {request.is_resume_public === 1 && (
                      <p className="text-sm text-gray-500">
                        * سيكون هذا الملف متاحاً للجميع
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4 gap-2">
          <Button 
            onClick={handleApprove} 
            className="hover:bg-secondary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner className="ml-2" />
                جاري القبول...
              </>
            ) : (
              'قبول الطلب'
            )}
          </Button>
          <Button 
            variant="destructive" 
            onClick={onReject}
            disabled={isSubmitting}
          >
            رفض الطلب
          </Button>
          <Button 
            variant="ghost" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            إغلاق
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}